import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";
import { Trash2, Plus, ChevronDown, ChevronUp, Copy, AlertTriangle } from "lucide-react";
import {
  DECIMALES_PRESENTACION,
  formatear,
  num,
  patronesDePlantilla,
  regresionLineal,
} from "../../utils/calibCalculo";

const API = process.env.REACT_APP_API_URL;

const TIPOS_DATO = ["numerico", "texto", "booleano", "seleccion"];
const CRITERIOS = [
  { value: "todas_conformes", label: "Todas las variables deben ser conformes" },
  { value: "mayoria_conforme", label: "Mayoría debe ser conforme" },
  { value: "criticas_conformes", label: "Solo variables críticas deben ser conformes" },
];
const REGLAS_TOLERANCIA = [
  { value: "mayor", label: "La mayor entre absoluta y %" },
  { value: "menor", label: "La menor entre absoluta y %" },
  { value: "suma", label: "Absoluta + % (a + b%)" },
];
const REGLAS_DECISION = [
  { value: "simple", label: "Aceptación simple (|E| ≤ tolerancia)" },
  { value: "banda_guarda", label: "Banda de guarda (|E| + U ≤ tolerancia)" },
];
const METODOS = [
  { value: "ebc_mide", label: "El equipo mide; el patrón genera la referencia" },
  { value: "ebc_genera", label: "El equipo genera; el patrón mide (p. ej. energía)" },
];
const INCERTIDUMBRE_DEFECTO = { calcular: true, kModo: "fijo", kFijo: 2 };
const METODOS_CORRECCION = [
  { value: "regresion", label: "Regresión lineal (recomendado; incluye incertidumbre del ajuste)" },
  { value: "puntual", label: "Corrección por punto (valor exacto del certificado)" },
  { value: "interpolacion", label: "Interpolación lineal entre puntos" },
  { value: "ninguna", label: "Sin corrección (solo incertidumbre)" },
];
// Secciones fijas del reporte que sirven de ancla a los parámetros fijos
// (mismo orden que en el PDF; claves de SECCIONES_REPORTE_CALIB en el servidor)
const SECCIONES_REPORTE = [
  { value: "cliente_equipo", label: "Cliente y equipo" },
  { value: "cronograma", label: "Cronograma" },
  { value: "gestion", label: "Gestión" },
  { value: "patrones", label: "Patrones utilizados" },
  { value: "ambiente", label: "Condiciones ambientales" },
  { value: "resultados", label: "Resultados de medición" },
  { value: "incertidumbre", label: "Incertidumbre y regla de decisión" },
  { value: "resultado_global", label: "Resultado global" },
  { value: "autorizacion", label: "Autorización" },
];
const UBICACIONES_PARAM = SECCIONES_REPORTE.flatMap(({ value, label }) => [
  { value: `antes:${value}`, label: `Encima de «${label}»` },
  { value: `despues:${value}`, label: `Debajo de «${label}»` },
]);
const PARAM_FIJO_DEFECTO = { seccion: "patrones", posicion: "antes", formato: "tabla" };

// Variable de medición del patrón: clave única patrón + variable
const claveVar = (patronId, varId) => `${patronId}:${varId}`;
const mismoValor = (a, b) => num(a) !== null && num(a) === num(b);
const txt = (v) => (v === null || v === undefined ? "" : String(v));
const norm = (s) => String(s || "").trim().toLowerCase();

const formVacio = (cat) => ({
  nombre: `Plantilla de calibración - ${cat?.name || ""}`,
  version: "1.0",
  estado: "borrador",
  repeticionesPorPunto: 3,
  instrucciones: "",
  variables: [],
  parametrosFijos: [],
  reglasAceptacion: {
    criterioGlobal: "todas_conformes",
    reglaTolerancia: "mayor",
    reglaDecision: "simple",
    descripcion: "",
  },
  incertidumbre: { ...INCERTIDUMBRE_DEFECTO },
  patronesSugeridos: [],
});

// Pendiente e intercepto de la corrección del patrón (C = −error) frente al
// valor nominal, como PENDIENTE/INTERSECCION.EJE del formato Excel. Usa todos
// los puntos de la variable del patrón, igual que el motor al corregir.
const RegresionPatron = ({ vp }) => {
  const pts = (vp.puntos || [])
    .map((p) => ({ x: num(p.valor), e: num(p.error) }))
    .filter((p) => p.x !== null && p.e !== null);
  const reg = regresionLineal(
    pts.map((p) => p.x),
    pts.map((p) => -p.e),
  );
  const f = (x) => formatear(x, DECIMALES_PRESENTACION);
  // Pendiente e intercepto: 6 cifras significativas (a 2 decimales se pierden)
  const fRecta = (x) => formatear(x, null);
  return (
    <div className="mt-2 border border-indigo-100 bg-indigo-50/40 rounded px-2 py-1.5 text-xs">
      <div className="font-medium text-indigo-800 mb-0.5">
        Ajuste lineal del patrón (corrección = pendiente · valor + intercepto)
      </div>
      {reg ? (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-gray-700">
          <span>Pendiente: <b>{fRecta(reg.m)}</b></span>
          <span>Intercepto: <b>{fRecta(reg.b)}</b></span>
          <span>s<sub>est</sub>: <b>{f(reg.sEst)}</b></span>
          <span className="text-gray-500">n = {reg.n}</span>
        </div>
      ) : (
        <div className="text-gray-500">
          Registre el error del patrón en al menos 2 puntos con valores distintos para calcularlo.
        </div>
      )}
      {reg && vp.metodoCorreccion && vp.metodoCorreccion !== "regresion" && (
        <div className="text-[11px] text-gray-500 mt-0.5">
          Informativo: la corrección configurada no es por regresión lineal.
        </div>
      )}
    </div>
  );
};

// Error, U y k del patrón en un punto de la plantilla (se guardan en la
// variable del patrón, por valor nominal, y aplican a todas sus plantillas)
const CamposPatronPunto = ({ vp, valor, onChange }) => {
  const pp = (vp.puntos || []).find((x) => mismoValor(x.valor, valor));
  const valido = num(valor) !== null;
  return (
    <span className="flex gap-1 items-center bg-indigo-50 border border-indigo-100 rounded px-1 py-0.5">
      {[
        ["error", "Error"],
        ["incertidumbre", "U"],
        ["k", "k"],
      ].map(([c, l]) => (
        <input
          key={c}
          type="number"
          step="any"
          disabled={!valido}
          title={valido ? `${l} del patrón en este punto (según su certificado)` : "Defina primero el valor numérico del punto"}
          placeholder={l}
          className="border border-indigo-200 rounded px-2 py-1 text-xs w-16 sm:w-20 shrink-0 bg-white disabled:bg-gray-100"
          value={txt(pp?.[c])}
          onChange={(e) => onChange(c, e.target.value)}
        />
      ))}
    </span>
  );
};

const variableVacia = () => ({
  nombre: "",
  unidad: "",
  unidadesPermitidas: [],
  tipoDato: "numerico",
  rangoMin: "",
  rangoMax: "",
  toleranciaAbsoluta: "",
  toleranciaPorcentual: "",
  resolucion: "",
  magnitud: "",
  metodo: "ebc_mide",
  capturaPatron: false,
  critica: false,
  patron: "",
  patronVariable: "",
  puntosCalib: [],
  obligatorio: true,
  orden: 0,
  observaciones: "",
  _expanded: true,
});

// "" / null / undefined → null (evita que Number(null) guarde 0)
const toNumOrNull = (v) =>
  v === "" || v === null || v === undefined || isNaN(Number(v)) ? null : Number(v);

const CalibTemplateForm = ({ cat, categorias = [], onClose, onTemplateSaved }) => {
  const [loading, setLoading] = useState(false);
  const [templateExistente, setTemplateExistente] = useState(null);
  const [generandoIA, setGenerandoIA] = useState(false);
  const [plantillas, setPlantillas] = useState([]); // de la categoría (una por conjunto de patrones)
  const [patrones, setPatrones] = useState([]); // con sus variablesMedicion
  const [sucios, setSucios] = useState({}); // variables del patrón editadas sin guardar
  const [usos, setUsos] = useState({}); // claveVar → plantillas que usan la variable del patrón
  const [copia, setCopia] = useState(null); // { modo, origen, destino, categoria }

  const planActual = localStorage.getItem("planActual") || "FREE";
  const isFreePlan = planActual === "FREE";
  const token = localStorage.getItem("token");

  const [form, setForm] = useState(() => formVacio(cat));
  const headers = { Authorization: `Bearer ${token}` };

  const cargarPlantilla = (t) => {
    setTemplateExistente(t || null);
    if (!t) return setForm(formVacio(cat));
    setForm({
      nombre: t.nombre,
      version: t.version,
      estado: t.estado,
      repeticionesPorPunto: t.repeticionesPorPunto,
      instrucciones: t.instrucciones || "",
      variables: (t.variables || []).map((v) => ({
        ...v,
        patron: v.patron?._id || v.patron || "",
        patronVariable: v.patronVariable || "",
        _expanded: false,
      })),
      parametrosFijos: t.parametrosFijos || [],
      reglasAceptacion: {
        criterioGlobal: "todas_conformes",
        reglaTolerancia: "mayor",
        reglaDecision: "simple",
        descripcion: "",
        ...(t.reglasAceptacion || {}),
      },
      incertidumbre: { ...INCERTIDUMBRE_DEFECTO, ...(t.incertidumbre || {}) },
      patronesSugeridos: t.patronesSugeridos || [],
    });
  };

  const cargarPatrones = async () => {
    const res = await axios.get(`${API}/calib-patrones`, { headers });
    setPatrones(res.data || []);
    setSucios({});
  };

  // Plantillas de la categoría y patrones con sus variables de medición
  useEffect(() => {
    if (!cat?._id) return;
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/calib-templates?categoriaId=${cat._id}`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${API}/calib-patrones`, { headers: h }).catch(() => ({ data: [] })),
    ]).then(([tRes, pRes]) => {
      const lista = tRes.data || [];
      setPlantillas(lista);
      setPatrones(pRes.data || []);
      cargarPlantilla(lista.find((t) => t.estado === "activo") || lista[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, token]);

  // Plantillas que comparten cada variable del patrón vinculada
  const clavesVinculadas = [
    ...new Set(
      form.variables
        .filter((v) => v.patron && v.patronVariable)
        .map((v) => claveVar(v.patron, v.patronVariable)),
    ),
  ].join(",");
  useEffect(() => {
    clavesVinculadas
      .split(",")
      .filter((k) => k && usos[k] === undefined)
      .forEach((k) => {
        const [pid, vid] = k.split(":");
        axios
          .get(`${API}/calib-patrones/${pid}/variables/${vid}/uso`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((r) => setUsos((prev) => ({ ...prev, [k]: r.data || [] })))
          .catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clavesVinculadas]);

  const patronPorId = (id) => patrones.find((p) => String(p._id) === String(id)) || null;
  const variablePatronDe = (v) =>
    (patronPorId(v.patron)?.variablesMedicion || []).find(
      (x) => String(x._id) === String(v.patronVariable),
    ) || null;

  const editarVariablePatron = (patronId, varId, cambio) => {
    setPatrones((prev) =>
      prev.map((p) =>
        String(p._id) !== String(patronId)
          ? p
          : {
              ...p,
              variablesMedicion: p.variablesMedicion.map((x) =>
                String(x._id) !== String(varId) ? x : cambio(x),
              ),
            },
      ),
    );
    setSucios((prev) => ({ ...prev, [claveVar(patronId, varId)]: true }));
  };

  const setDatoPatron = (v, valor, campo, valorCampo) =>
    editarVariablePatron(v.patron, v.patronVariable, (x) => {
      const puntos = [...(x.puntos || [])];
      const i = puntos.findIndex((p) => mismoValor(p.valor, valor));
      if (i >= 0) puntos[i] = { ...puntos[i], [campo]: valorCampo };
      else puntos.push({ valor: num(valor), error: "", incertidumbre: "", k: "", [campo]: valorCampo });
      return { ...x, puntos };
    });

  // Reemplaza un patrón con la respuesta del servidor sin perder ediciones pendientes
  const fusionarPatron = (doc) =>
    setPatrones((prev) =>
      prev.map((p) =>
        String(p._id) !== String(doc._id)
          ? p
          : {
              ...doc,
              variablesMedicion: (doc.variablesMedicion || []).map((v) =>
                sucios[claveVar(doc._id, v._id)]
                  ? p.variablesMedicion.find((x) => String(x._id) === String(v._id)) || v
                  : v,
              ),
            },
      ),
    );

  const guardarVariablesPatron = async () => {
    for (const k of Object.keys(sucios)) {
      const [pid, vid] = k.split(":");
      const p = patronPorId(pid);
      const vp = (p?.variablesMedicion || []).find((x) => String(x._id) === vid);
      if (!vp) continue;
      try {
        await axios.put(
          `${API}/calib-patrones/${pid}/variables/${vid}`,
          { metodoCorreccion: vp.metodoCorreccion, resolucion: vp.resolucion, puntos: vp.puntos },
          { headers },
        );
      } catch (err) {
        const errores = err.response?.data?.errores;
        toast.error(
          `${p.nombre} · ${vp.nombre}: ${errores?.[0] || err.response?.data?.message || "error al guardar"}`,
        );
        return false;
      }
    }
    return true;
  };

  const handleGenerarIA = async () => {
    if (!cat?._id) return;
    try {
      setGenerandoIA(true);
      const res = await axios.post(
        `${API}/ia/calibraciones/template/generar/${cat._id}`,
        { grupo: cat.grupo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      setForm((prev) => ({
        ...prev,
        nombre: data.nombre || prev.nombre,
        version: data.version || prev.version,
        instrucciones: data.instrucciones || prev.instrucciones,
        repeticionesPorPunto: data.repeticionesPorPunto || prev.repeticionesPorPunto,
        variables: (data.variables || []).map((v, i) => ({
          ...variableVacia(),
          ...v,
          orden: i + 1,
          _expanded: true,
        })),
        parametrosFijos: data.parametrosFijos || prev.parametrosFijos,
        reglasAceptacion: data.reglasAceptacion || prev.reglasAceptacion,
        patronesSugeridos: data.patronesSugeridos || prev.patronesSugeridos,
      }));
      toast.success("Plantilla generada con IA. Revisa antes de guardar.");
    } catch {
      toast.error("No se pudo generar la plantilla con IA");
    } finally {
      setGenerandoIA(false);
    }
  };

  const handleGuardar = async () => {
    if (!form.nombre.trim()) return toast.warning("El nombre es requerido");
    if (form.variables.length === 0) return toast.warning("Agrega al menos una variable");

    const payload = {
      ...form,
      categoria: cat._id,
      variables: form.variables.map(({ _expanded, ...v }) => ({
        ...v,
        rangoMin: toNumOrNull(v.rangoMin),
        rangoMax: toNumOrNull(v.rangoMax),
        toleranciaAbsoluta: toNumOrNull(v.toleranciaAbsoluta),
        toleranciaPorcentual: toNumOrNull(v.toleranciaPorcentual),
        resolucion: toNumOrNull(v.resolucion),
        puntosCalib: (v.puntosCalib || []).map((p) => ({
          ...p,
          valor:
            (v.tipoDato || "numerico") === "numerico" && toNumOrNull(p.valor) !== null
              ? toNumOrNull(p.valor)
              : p.valor,
          toleranciaAbsoluta: toNumOrNull(p.toleranciaAbsoluta),
          toleranciaPorcentual: toNumOrNull(p.toleranciaPorcentual),
        })),
      })),
    };

    try {
      setLoading(true);
      // Datos del patrón primero: la plantilla los referencia
      if (!(await guardarVariablesPatron())) return;
      if (templateExistente) {
        await axios.put(`${API}/calib-templates/${templateExistente._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Plantilla actualizada correctamente");
      } else {
        await axios.post(`${API}/calib-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Plantilla creada correctamente");
      }
      if (onTemplateSaved) onTemplateSaved();
      onClose();
    } catch (err) {
      const errores = err.response?.data?.errores;
      toast.error(
        errores?.length > 1
          ? `${errores[0]} (y ${errores.length - 1} más)`
          : err.response?.data?.message || "Error al guardar la plantilla",
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Variables helpers ──────────────────────────────────────
  const addVariable = () => {
    setForm((prev) => ({
      ...prev,
      variables: [...prev.variables, { ...variableVacia(), orden: prev.variables.length + 1 }],
    }));
  };

  const removeVariable = (idx) => {
    setForm((prev) => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== idx),
    }));
  };

  const updateVariable = (idx, field, value) => {
    setForm((prev) => {
      const vars = [...prev.variables];
      vars[idx] = { ...vars[idx], [field]: value };
      return { ...prev, variables: vars };
    });
  };

  const updateVariableCampos = (idx, campos) => {
    setForm((prev) => {
      const vars = [...prev.variables];
      vars[idx] = { ...vars[idx], ...campos };
      return { ...prev, variables: vars };
    });
  };

  // Al elegir el patrón se sugiere su variable equivalente (misma unidad y magnitud o nombre)
  const vincularPatron = (idx, patronId) => {
    const v = form.variables[idx];
    const candidatas = (patronPorId(patronId)?.variablesMedicion || []).filter(
      (x) => !v.unidad || norm(x.unidad) === norm(v.unidad),
    );
    const sugerida =
      candidatas.find(
        (x) =>
          (v.magnitud && norm(x.magnitud) === norm(v.magnitud)) || norm(x.nombre) === norm(v.nombre),
      ) || (candidatas.length === 1 ? candidatas[0] : null);
    updateVariableCampos(idx, { patron: patronId, patronVariable: sugerida?._id || "" });
  };

  const crearVariableEnPatron = async (idx) => {
    const v = form.variables[idx];
    if (!v.nombre?.trim() || !v.unidad?.trim()) {
      return toast.warning("Defina primero el nombre y la unidad de la variable");
    }
    try {
      const res = await axios.post(
        `${API}/calib-patrones/${v.patron}/variables`,
        { nombre: v.nombre, magnitud: v.magnitud, unidad: v.unidad },
        { headers },
      );
      const lista = res.data?.variablesMedicion || [];
      fusionarPatron(res.data);
      updateVariableCampos(idx, { patronVariable: lista[lista.length - 1]?._id || "" });
      toast.success("Variable creada en el patrón");
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo crear la variable en el patrón");
    }
  };

  const copiarPlantilla = async () => {
    const body =
      copia.modo === "patron"
        ? { patronOrigen: copia.origen, patronDestino: copia.destino }
        : { categoria: copia.categoria };
    try {
      const res = await axios.post(`${API}/calib-templates/${templateExistente._id}/copiar`, body, {
        headers,
      });
      if (copia.modo === "patron") {
        setPlantillas((prev) => [res.data, ...prev]);
        await cargarPatrones(); // el destino pudo recibir variables nuevas
        cargarPlantilla(res.data);
        toast.success("Plantilla copiada en borrador. Complete el error y la U del nuevo patrón y actívela.");
      } else {
        const destino = categorias.find((c) => String(c._id) === String(copia.categoria));
        toast.success(`Plantilla copiada en borrador a ${destino?.name || "la categoría"}`);
        if (onTemplateSaved) onTemplateSaved();
      }
      setCopia(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo copiar la plantilla");
    }
  };

  const nombresPatronesDe = (t) =>
    patronesDePlantilla(t)
      .map((id) => patronPorId(id)?.nombre)
      .filter(Boolean)
      .join(", ");
  const haySucios = Object.keys(sucios).length > 0;

  const toggleExpandVariable = (idx) => {
    setForm((prev) => {
      const vars = [...prev.variables];
      vars[idx] = { ...vars[idx], _expanded: !vars[idx]._expanded };
      return { ...prev, variables: vars };
    });
  };

  const addPunto = (idx) => {
    updateVariable(idx, "puntosCalib", [
      ...(form.variables[idx].puntosCalib || []),
      { valor: "", etiqueta: "", toleranciaAbsoluta: "", toleranciaPorcentual: "" },
    ]);
  };

  const updatePunto = (varIdx, puntoIdx, field, value) => {
    setForm((prev) => {
      const vars = [...prev.variables];
      const puntos = [...(vars[varIdx].puntosCalib || [])];
      puntos[puntoIdx] = { ...puntos[puntoIdx], [field]: value };
      vars[varIdx] = { ...vars[varIdx], puntosCalib: puntos };
      return { ...prev, variables: vars };
    });
  };

  const removePunto = (varIdx, puntoIdx) => {
    setForm((prev) => {
      const vars = [...prev.variables];
      vars[varIdx] = {
        ...vars[varIdx],
        puntosCalib: vars[varIdx].puntosCalib.filter((_, i) => i !== puntoIdx),
      };
      return { ...prev, variables: vars };
    });
  };

  // ── Parámetros fijos helpers ────────────────────────────────
  const addParamFijo = () => {
    setForm((prev) => ({
      ...prev,
      parametrosFijos: [
        ...prev.parametrosFijos,
        { clave: "", etiqueta: "", valor: "", obligatorio: false, ...PARAM_FIJO_DEFECTO },
      ],
    }));
  };

  const updateParamFijo = (idx, field, value) => {
    setForm((prev) => {
      const params = [...prev.parametrosFijos];
      params[idx] = { ...params[idx], [field]: value };
      return { ...prev, parametrosFijos: params };
    });
  };

  // "antes:patrones" → { posicion: "antes", seccion: "patrones" }
  const updateUbicacionParam = (idx, valor) => {
    const [posicion, seccion] = valor.split(":");
    setForm((prev) => {
      const params = [...prev.parametrosFijos];
      params[idx] = { ...params[idx], posicion, seccion };
      return { ...prev, parametrosFijos: params };
    });
  };

  const moveParamFijo = (idx, delta) => {
    setForm((prev) => {
      const params = [...prev.parametrosFijos];
      const destino = idx + delta;
      if (destino < 0 || destino >= params.length) return prev;
      [params[idx], params[destino]] = [params[destino], params[idx]];
      return { ...prev, parametrosFijos: params };
    });
  };

  const removeParamFijo = (idx) => {
    setForm((prev) => ({
      ...prev,
      parametrosFijos: prev.parametrosFijos.filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-2">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-3 sm:p-5">
            {/* Header */}
            <div className="flex justify-between items-start gap-3 mb-4">
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-gray-800 break-words">
                  Plantilla de Calibración — {cat?.name}
                </h2>
                <p className="text-xs text-gray-500">
                  {templateExistente ? "Editando plantilla existente" : "Nueva plantilla"}
                </p>
              </div>
              <button onClick={onClose} className="shrink-0 text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>

            {/* Plantillas de la categoría: una activa por conjunto de patrones */}
            <div className="flex flex-wrap items-center gap-2 mb-4 p-2 bg-gray-50 border rounded-lg">
              <label className="text-xs text-gray-600 font-medium">Plantilla</label>
              <select
                className="border rounded px-2 py-1.5 text-sm flex-1 min-w-[12rem] bg-white"
                value={templateExistente?._id || ""}
                onChange={(e) => cargarPlantilla(plantillas.find((t) => t._id === e.target.value))}
              >
                {plantillas.map((t) => {
                  const nombres = nombresPatronesDe(t);
                  return (
                    <option key={t._id} value={t._id}>
                      {t.nombre} · v{t.version} · {t.estado}
                      {nombres ? ` · ${nombres}` : ""}
                    </option>
                  );
                })}
                <option value="">+ Nueva plantilla</option>
              </select>
              {templateExistente && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={patronesDePlantilla(templateExistente).length === 0}
                    title={
                      patronesDePlantilla(templateExistente).length === 0
                        ? "La plantilla no tiene variables vinculadas a un patrón"
                        : "Duplicar la plantilla reemplazando uno de sus patrones"
                    }
                    onClick={() =>
                      setCopia({ modo: "patron", origen: patronesDePlantilla(templateExistente)[0], destino: "" })
                    }
                  >
                    <Copy size={13} className="mr-1" /> Copiar para otro patrón
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setCopia({ modo: "categoria", categoria: "" })}
                  >
                    <Copy size={13} className="mr-1" /> Copiar para otra categoría
                  </Button>
                </>
              )}
              <p className="w-full text-[11px] text-gray-500">
                Puede haber varias plantillas activas en la categoría, una por patrón (o conjunto de
                patrones). Al calibrar se usa la que corresponde a los patrones seleccionados.
              </p>
            </div>

            {copia && (
              <div className="mb-4 p-3 border border-blue-200 bg-blue-50 rounded-lg text-sm space-y-2">
                <div className="font-medium text-blue-900">
                  {copia.modo === "patron" ? "Copiar para otro patrón" : "Copiar para otra categoría"}
                </div>
                {copia.modo === "patron" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="text-xs text-gray-600">
                      Reemplazar el patrón
                      <select
                        className="border rounded w-full px-2 py-1.5 text-sm mt-0.5 bg-white"
                        value={copia.origen}
                        onChange={(e) => setCopia((c) => ({ ...c, origen: e.target.value }))}
                      >
                        {patronesDePlantilla(templateExistente).map((id) => (
                          <option key={id} value={id}>{patronPorId(id)?.nombre || id}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-gray-600">
                      por
                      <select
                        className="border rounded w-full px-2 py-1.5 text-sm mt-0.5 bg-white"
                        value={copia.destino}
                        onChange={(e) => setCopia((c) => ({ ...c, destino: e.target.value }))}
                      >
                        <option value="">Seleccione…</option>
                        {patrones
                          .filter((p) => String(p._id) !== String(copia.origen))
                          .map((p) => (
                            <option key={p._id} value={p._id}>{p.nombre}</option>
                          ))}
                      </select>
                    </label>
                    <p className="sm:col-span-2 text-[11px] text-gray-600">
                      Las variables se asocian a las del nuevo patrón con la misma magnitud y unidad; las
                      que no existan se crean vacías (sin error ni U: cada patrón tiene su certificado).
                    </p>
                  </div>
                ) : (
                  <label className="text-xs text-gray-600 block">
                    Categoría destino
                    <select
                      className="border rounded w-full px-2 py-1.5 text-sm mt-0.5 bg-white"
                      value={copia.categoria}
                      onChange={(e) => setCopia((c) => ({ ...c, categoria: e.target.value }))}
                    >
                      <option value="">Seleccione…</option>
                      {categorias
                        .filter((c) => String(c._id) !== String(cat?._id))
                        .map((c) => (
                          <option key={c._id} value={c._id}>{c.name}</option>
                        ))}
                    </select>
                  </label>
                )}
                <p className="text-[11px] text-gray-500">
                  Se copia la versión guardada de la plantilla y queda en borrador.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button type="button" size="sm" variant="outline" onClick={() => setCopia(null)}>
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={copia.modo === "patron" ? !copia.destino || haySucios : !copia.categoria}
                    title={haySucios ? "Guarde primero los cambios de los patrones" : undefined}
                    onClick={copiarPlantilla}
                  >
                    Copiar
                  </Button>
                </div>
              </div>
            )}

            {/* Datos generales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-600 font-medium">Nombre de plantilla</label>
                <input
                  type="text"
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.nombre}
                  onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Versión</label>
                <input
                  type="text"
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.version}
                  onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Estado</label>
                <select
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.estado}
                  onChange={(e) => setForm((p) => ({ ...p, estado: e.target.value }))}
                >
                  <option value="borrador">Borrador</option>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Repeticiones por punto</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.repeticionesPorPunto}
                  onChange={(e) => setForm((p) => ({ ...p, repeticionesPorPunto: Number(e.target.value) }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Criterio de aceptación</label>
                <select
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.reglasAceptacion.criterioGlobal}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      reglasAceptacion: { ...p.reglasAceptacion, criterioGlobal: e.target.value },
                    }))
                  }
                >
                  {CRITERIOS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Combinación de tolerancias</label>
                <select
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.reglasAceptacion.reglaTolerancia || "mayor"}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      reglasAceptacion: { ...p.reglasAceptacion, reglaTolerancia: e.target.value },
                    }))
                  }
                >
                  {REGLAS_TOLERANCIA.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Regla de decisión</label>
                <select
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.reglasAceptacion.reglaDecision || "simple"}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      reglasAceptacion: { ...p.reglasAceptacion, reglaDecision: e.target.value },
                    }))
                  }
                >
                  {REGLAS_DECISION.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Factor de cobertura k</label>
                <div className="flex gap-2 mt-1">
                  <select
                    className="border rounded-lg flex-1 min-w-0 px-3 py-2 text-sm"
                    value={form.incertidumbre.kModo}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        incertidumbre: { ...p.incertidumbre, kModo: e.target.value },
                      }))
                    }
                  >
                    <option value="fijo">Fijo</option>
                    <option value="veff">Por grados de libertad efectivos (95 %)</option>
                  </select>
                  {form.incertidumbre.kModo === "fijo" && (
                    <input
                      type="number"
                      step="any"
                      min={1}
                      max={5}
                      className="border rounded-lg w-20 shrink-0 px-2 py-2 text-sm"
                      value={form.incertidumbre.kFijo}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          incertidumbre: { ...p.incertidumbre, kFijo: e.target.value },
                        }))
                      }
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1 sm:mt-6 lg:mt-0">
                <input
                  type="checkbox"
                  id="calcular-incertidumbre"
                  checked={form.incertidumbre.calcular !== false}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      incertidumbre: { ...p.incertidumbre, calcular: e.target.checked },
                    }))
                  }
                />
                <label htmlFor="calcular-incertidumbre" className="text-xs text-gray-600">
                  Calcular incertidumbre (necesario para emitir certificado)
                </label>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="text-xs text-gray-600 font-medium">Instrucciones generales</label>
                <textarea
                  rows={2}
                  className="border rounded-lg w-full px-3 py-2 mt-1 text-sm"
                  value={form.instrucciones}
                  onChange={(e) => setForm((p) => ({ ...p, instrucciones: e.target.value }))}
                  placeholder="Instrucciones para el técnico que ejecuta la calibración..."
                />
              </div>
            </div>

            {/* IA Button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
              <div className="relative self-start shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { if (!isFreePlan) handleGenerarIA(); }}
                  disabled={isFreePlan || generandoIA}
                  className={isFreePlan ? "cursor-not-allowed opacity-60" : ""}
                >
                  {generandoIA ? "Generando con IA..." : "Generar con IA"}
                </Button>
                {isFreePlan && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full shadow">
                    Plan de pago
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                La IA sugiere variables, unidades y puntos de calibración. Revisa antes de activar.
              </p>
            </div>

            {/* Variables de medición */}
            <div className="mb-5">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <h3 className="font-semibold text-gray-700 text-sm">Variables de medición</h3>
                <Button type="button" size="sm" onClick={addVariable}>
                  <Plus size={14} className="mr-1" /> Agregar variable
                </Button>
              </div>

              {form.variables.length === 0 && (
                <p className="text-gray-400 italic text-sm">No hay variables. Agrega o genera con IA.</p>
              )}

              <div className="space-y-2">
                {form.variables.map((v, idx) => (
                  <div key={idx} className="border rounded-lg overflow-hidden">
                    {/* Variable header */}
                    <div
                      className="flex justify-between items-center gap-2 px-3 py-2 bg-gray-50 cursor-pointer"
                      onClick={() => toggleExpandVariable(idx)}
                    >
                      <span className="min-w-0 break-words text-sm font-medium text-gray-700">
                        {v.nombre || `Variable ${idx + 1}`}
                        {v.unidad && <span className="ml-2 text-gray-400 font-normal">({v.unidad})</span>}
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeVariable(idx); }}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                        {v._expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>

                    {v._expanded && (
                      <div className="p-3 grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="min-[400px]:col-span-2">
                          <label className="text-xs text-gray-500">Nombre *</label>
                          <input
                            type="text"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.nombre}
                            onChange={(e) => updateVariable(idx, "nombre", e.target.value)}
                            placeholder="ej: Presión sistólica"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Unidad *</label>
                          <input
                            type="text"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.unidad}
                            onChange={(e) => updateVariable(idx, "unidad", e.target.value)}
                            placeholder="ej: mmHg"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Tipo de dato</label>
                          <select
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.tipoDato}
                            onChange={(e) => updateVariable(idx, "tipoDato", e.target.value)}
                          >
                            {TIPOS_DATO.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Rango mín</label>
                          <input
                            type="number"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.rangoMin}
                            onChange={(e) => updateVariable(idx, "rangoMin", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Rango máx</label>
                          <input
                            type="number"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.rangoMax}
                            onChange={(e) => updateVariable(idx, "rangoMax", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Tolerancia abs.</label>
                          <input
                            type="number"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.toleranciaAbsoluta}
                            onChange={(e) => updateVariable(idx, "toleranciaAbsoluta", e.target.value)}
                            placeholder="ej: 2"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Tolerancia %</label>
                          <input
                            type="number"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.toleranciaPorcentual}
                            onChange={(e) => updateVariable(idx, "toleranciaPorcentual", e.target.value)}
                            placeholder="ej: 2.5"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Resolución</label>
                          <input
                            type="number"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.resolucion}
                            onChange={(e) => updateVariable(idx, "resolucion", e.target.value)}
                            placeholder="ej: 0.1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Magnitud</label>
                          <input
                            type="text"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.magnitud || ""}
                            onChange={(e) => updateVariable(idx, "magnitud", e.target.value)}
                            placeholder="ej: Presión"
                            title="Debe coincidir con la magnitud del patrón"
                          />
                        </div>
                        <div className="min-[400px]:col-span-2">
                          <label className="text-xs text-gray-500">Método de medición</label>
                          <select
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.metodo || "ebc_mide"}
                            onChange={(e) => updateVariable(idx, "metodo", e.target.value)}
                          >
                            {METODOS.map((m) => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-2 mt-1 min-[400px]:mt-4">
                          <input
                            type="checkbox"
                            checked={v.capturaPatron === true}
                            disabled={v.metodo === "ebc_genera"}
                            onChange={(e) => updateVariable(idx, "capturaPatron", e.target.checked)}
                            id={`captura-${idx}`}
                          />
                          <label htmlFor={`captura-${idx}`} className="text-xs text-gray-600" title="Registrar en cada repetición también la lectura del patrón">
                            Registrar lectura del patrón
                          </label>
                        </div>
                        <div className="flex items-center gap-2 mt-1 min-[400px]:mt-4">
                          <input
                            type="checkbox"
                            checked={v.critica === true}
                            onChange={(e) => updateVariable(idx, "critica", e.target.checked)}
                            id={`critica-${idx}`}
                          />
                          <label htmlFor={`critica-${idx}`} className="text-xs text-gray-600">Variable crítica</label>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Orden</label>
                          <input
                            type="number"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.orden}
                            onChange={(e) => updateVariable(idx, "orden", Number(e.target.value))}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-1 min-[400px]:mt-4">
                          <input
                            type="checkbox"
                            checked={v.obligatorio}
                            onChange={(e) => updateVariable(idx, "obligatorio", e.target.checked)}
                            id={`oblig-${idx}`}
                          />
                          <label htmlFor={`oblig-${idx}`} className="text-xs text-gray-600">Obligatorio</label>
                        </div>
                        <div className="min-[400px]:col-span-2 sm:col-span-4">
                          <label className="text-xs text-gray-500">Observaciones de la variable</label>
                          <input
                            type="text"
                            className="border rounded w-full px-2 py-1 text-sm mt-0.5"
                            value={v.observaciones}
                            onChange={(e) => updateVariable(idx, "observaciones", e.target.value)}
                          />
                        </div>

                        {/* Patrón de referencia de la variable */}
                        {(() => {
                          const pat = patronPorId(v.patron);
                          const vp = variablePatronDe(v);
                          const k = vp ? claveVar(v.patron, v.patronVariable) : "";
                          const otras = (usos[k] || []).filter(
                            (t) => String(t._id) !== String(templateExistente?._id),
                          );
                          return (
                            <div className="min-[400px]:col-span-2 sm:col-span-4 border border-indigo-200 bg-indigo-50/40 rounded-lg p-2">
                              <div className="text-xs font-medium text-indigo-800 mb-1">Patrón de referencia</div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <select
                                  className="border rounded w-full px-2 py-1 text-sm bg-white"
                                  value={v.patron || ""}
                                  onChange={(e) => vincularPatron(idx, e.target.value)}
                                >
                                  <option value="">Sin vincular (se elige al calibrar; sin incertidumbre)</option>
                                  {patrones.map((p) => (
                                    <option key={p._id} value={p._id}>
                                      {p.nombre}
                                      {p.numeroCertificado ? ` · Cert. ${p.numeroCertificado}` : ""}
                                    </option>
                                  ))}
                                </select>
                                {v.patron && (
                                  <select
                                    className="border rounded w-full px-2 py-1 text-sm bg-white"
                                    value={v.patronVariable || ""}
                                    onChange={(e) =>
                                      e.target.value === "__nueva"
                                        ? crearVariableEnPatron(idx)
                                        : updateVariableCampos(idx, { patronVariable: e.target.value })
                                    }
                                  >
                                    <option value="">Variable del patrón…</option>
                                    {(pat?.variablesMedicion || []).map((x) => (
                                      <option key={x._id} value={x._id}>
                                        {x.nombre}
                                        {x.unidad ? ` (${x.unidad})` : ""}
                                      </option>
                                    ))}
                                    <option value="__nueva">+ Crear «{v.nombre || "variable"}» en este patrón</option>
                                  </select>
                                )}
                              </div>
                              {vp && (
                                <div className="mt-2 space-y-1">
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <label className="sm:col-span-2 text-xs text-gray-500">
                                      Corrección del patrón
                                      <select
                                        className="border rounded w-full px-2 py-1 text-sm mt-0.5 bg-white"
                                        value={vp.metodoCorreccion || "regresion"}
                                        onChange={(e) =>
                                          editarVariablePatron(v.patron, v.patronVariable, (x) => ({
                                            ...x,
                                            metodoCorreccion: e.target.value,
                                          }))
                                        }
                                      >
                                        {METODOS_CORRECCION.map((m) => (
                                          <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                      </select>
                                    </label>
                                    <label className="text-xs text-gray-500">
                                      Resolución del patrón
                                      <input
                                        type="number"
                                        step="any"
                                        className="border rounded w-full px-2 py-1 text-sm mt-0.5 bg-white"
                                        value={txt(vp.resolucion)}
                                        onChange={(e) =>
                                          editarVariablePatron(v.patron, v.patronVariable, (x) => ({
                                            ...x,
                                            resolucion: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                  </div>
                                  {v.unidad && vp.unidad && norm(v.unidad) !== norm(vp.unidad) && (
                                    <p className="text-[11px] text-red-600 flex items-center gap-1">
                                      <AlertTriangle size={11} /> La unidad del patrón ({vp.unidad}) no coincide con la de la variable ({v.unidad}).
                                    </p>
                                  )}
                                  {vp.certificado && pat?.numeroCertificado && vp.certificado !== pat.numeroCertificado && (
                                    <p className="text-[11px] text-amber-700 flex items-center gap-1">
                                      <AlertTriangle size={11} /> Error y U cargados con el certificado {vp.certificado}; el
                                      patrón tiene ahora el certificado {pat.numeroCertificado}. Actualícelos.
                                    </p>
                                  )}
                                  <p className="text-[11px] text-indigo-700">
                                    Error, U y k del patrón se registran por punto en la tabla de abajo y aplican a
                                    todas las plantillas que usan esta variable del patrón
                                    {otras.length ? ` (también: ${otras.map((t) => t.nombre).join(", ")})` : ""}.
                                    {sucios[k] ? " Cambios pendientes de guardar." : ""}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Puntos de calibración */}
                        <div className="min-[400px]:col-span-2 sm:col-span-4">
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-medium text-gray-600">Puntos de calibración</label>
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:underline"
                              onClick={() => addPunto(idx)}
                            >
                              + Punto
                            </button>
                          </div>
                          {(v.puntosCalib || []).length > 0 && (
                            <p className="text-[10px] text-gray-400 mb-1">
                              Tolerancias del punto opcionales: si se define alguna, reemplaza las de la variable.
                              {variablePatronDe(v) ? " En azul: error, U y k del patrón en ese punto." : ""}
                            </p>
                          )}
                          {(v.puntosCalib || []).map((p, pi) => (
                            <div key={pi} className="flex gap-2 mb-1 items-start">
                              <div className="flex flex-1 min-w-0 flex-wrap gap-2 items-center">
                              <input
                                type="text"
                                placeholder="Valor"
                                className="border rounded px-2 py-1 text-xs w-20 sm:w-24 shrink-0"
                                value={p.valor}
                                onChange={(e) => updatePunto(idx, pi, "valor", e.target.value)}
                              />
                              <input
                                type="text"
                                placeholder="Etiqueta (ej: Cero)"
                                className="border rounded px-2 py-1 text-xs flex-1 min-w-[6rem]"
                                value={p.etiqueta}
                                onChange={(e) => updatePunto(idx, pi, "etiqueta", e.target.value)}
                              />
                              <input
                                type="number"
                                step="any"
                                title="Tolerancia absoluta del punto (opcional)"
                                placeholder={`± abs${v.toleranciaAbsoluta !== "" && v.toleranciaAbsoluta != null ? ` (${v.toleranciaAbsoluta})` : ""}`}
                                className="border rounded px-2 py-1 text-xs w-20 sm:w-24 shrink-0"
                                value={p.toleranciaAbsoluta ?? ""}
                                onChange={(e) => updatePunto(idx, pi, "toleranciaAbsoluta", e.target.value)}
                              />
                              <input
                                type="number"
                                step="any"
                                title="Tolerancia porcentual del punto (opcional)"
                                placeholder={`± %${v.toleranciaPorcentual !== "" && v.toleranciaPorcentual != null ? ` (${v.toleranciaPorcentual})` : ""}`}
                                className="border rounded px-2 py-1 text-xs w-20 sm:w-24 shrink-0"
                                value={p.toleranciaPorcentual ?? ""}
                                onChange={(e) => updatePunto(idx, pi, "toleranciaPorcentual", e.target.value)}
                              />
                              {variablePatronDe(v) && (
                                <CamposPatronPunto
                                  vp={variablePatronDe(v)}
                                  valor={p.valor}
                                  onChange={(campo, valor) => setDatoPatron(v, p.valor, campo, valor)}
                                />
                              )}
                              </div>
                              <button
                                type="button"
                                onClick={() => removePunto(idx, pi)}
                                className="shrink-0 mt-1.5 text-red-400 hover:text-red-600"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                          {variablePatronDe(v) && <RegresionPatron vp={variablePatronDe(v)} />}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Parámetros fijos del reporte */}
            <div className="mb-5">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <h3 className="font-semibold text-gray-700 text-sm">Parámetros fijos del reporte</h3>
                <Button type="button" size="sm" variant="outline" onClick={addParamFijo}>
                  + Parámetro
                </Button>
              </div>
              {form.parametrosFijos.length > 0 && (
                <p className="text-xs text-gray-500 mb-2">
                  Define dónde aparece cada parámetro en el reporte y cómo se muestra: como fila
                  de la tabla «Parámetros del reporte» o como bloque independiente (título y
                  descripción). Los parámetros de una misma ubicación salen en el orden de la lista.
                </p>
              )}
              {form.parametrosFijos.map((p, idx) => (
                <div key={idx} className="border rounded-lg p-2 mb-2 space-y-2">
                <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Clave (ej: normaTecnica)"
                    className="border rounded px-2 py-1.5 text-xs w-full sm:w-36 sm:shrink-0"
                    value={p.clave}
                    onChange={(e) => updateParamFijo(idx, "clave", e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Etiqueta visible"
                    className="border rounded px-2 py-1.5 text-xs w-full sm:w-auto sm:flex-1 min-w-0"
                    value={p.etiqueta}
                    onChange={(e) => updateParamFijo(idx, "etiqueta", e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Valor por defecto"
                    className="border rounded px-2 py-1.5 text-xs flex-1 min-w-0"
                    value={p.valor}
                    onChange={(e) => updateParamFijo(idx, "valor", e.target.value)}
                  />
                  <div className="flex shrink-0 items-center gap-1">
                    <input
                      type="checkbox"
                      checked={p.obligatorio}
                      onChange={(e) => updateParamFijo(idx, "obligatorio", e.target.checked)}
                      id={`pf-oblig-${idx}`}
                    />
                    <label htmlFor={`pf-oblig-${idx}`} className="text-xs text-gray-500">Req</label>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeParamFijo(idx)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
                  <label className="text-xs text-gray-500 shrink-0" htmlFor={`pf-ubic-${idx}`}>
                    Ubicación
                  </label>
                  <select
                    id={`pf-ubic-${idx}`}
                    className="border rounded px-2 py-1.5 text-xs w-full sm:w-auto sm:flex-1 min-w-0"
                    value={`${p.posicion || PARAM_FIJO_DEFECTO.posicion}:${p.seccion || PARAM_FIJO_DEFECTO.seccion}`}
                    onChange={(e) => updateUbicacionParam(idx, e.target.value)}
                  >
                    {UBICACIONES_PARAM.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                  <label className="text-xs text-gray-500 shrink-0" htmlFor={`pf-formato-${idx}`}>
                    Formato
                  </label>
                  <select
                    id={`pf-formato-${idx}`}
                    className="border rounded px-2 py-1.5 text-xs w-full sm:w-52"
                    value={p.formato || PARAM_FIJO_DEFECTO.formato}
                    onChange={(e) => updateParamFijo(idx, "formato", e.target.value)}
                  >
                    <option value="tabla">Fila de tabla</option>
                    <option value="independiente">Independiente (título + descripción)</option>
                  </select>
                  <div className="flex shrink-0 items-center gap-1 ml-auto">
                    <button
                      type="button"
                      onClick={() => moveParamFijo(idx, -1)}
                      disabled={idx === 0}
                      className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      title="Subir"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveParamFijo(idx, 1)}
                      disabled={idx === form.parametrosFijos.length - 1}
                      className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      title="Bajar"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                </div>
                </div>
              ))}
            </div>

            {/* Patrones sugeridos */}
            {form.patronesSugeridos?.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-xs font-semibold text-amber-800 mb-1">Patrones sugeridos por IA</h4>
                {form.patronesSugeridos.map((ps, i) => (
                  <p key={i} className="text-xs text-amber-700">
                    • <strong>{ps.tipo}</strong>: {ps.descripcion} {ps.incertidumbreMaxima && `— máx. incertidumbre: ${ps.incertidumbreMaxima}`}
                  </p>
                ))}
              </div>
            )}

            {/* Acciones */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t mt-4">
              <Button variant="outline" onClick={onClose} type="button" className="w-full sm:w-auto">
                Cancelar
              </Button>
              <Button onClick={handleGuardar} disabled={loading} className="w-full sm:w-auto">
                {loading ? "Guardando..." : templateExistente ? "Actualizar plantilla" : "Crear plantilla"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CalibTemplateForm;
